import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import CityAutocomplete from "../components/CityAutocomplete";
import { Bell, Plus, Trash2, AlertCircle, MapPin, Tag, Send, Check, Smartphone } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported,
  notificationPermission,
  getCurrentSubscription,
  subscribeToPush,
  unsubscribeFromPush,
} from "../lib/pwa";

export default function AlertsPage() {
  const { t } = useI18n();
  const { user, login } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [city, setCity] = useState("");
  const [tg, setTg] = useState({ linked: false, bot_username: "" });
  const [tgDeepLink, setTgDeepLink] = useState("");
  const [tgPolling, setTgPolling] = useState(false);
  const [push, setPush] = useState({ supported: false, subscribed: false, perm: "default" });
  const [pushBusy, setPushBusy] = useState(false);

  // Detect web-push capability + current subscription on mount.
  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    (async () => {
      const supported = isPushSupported();
      if (!supported) {
        if (alive) setPush({ supported: false, subscribed: false, perm: "default" });
        return;
      }
      const sub = await getCurrentSubscription();
      if (alive) {
        setPush({
          supported: true,
          subscribed: !!sub,
          perm: notificationPermission(),
        });
      }
    })();
    return () => { alive = false; };
  }, [user]);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (push.subscribed) {
        await unsubscribeFromPush();
        setPush({ ...push, subscribed: false });
        toast.success(t("push_disabled"));
      } else {
        await subscribeToPush();
        setPush({ ...push, subscribed: true, perm: "granted" });
        toast.success(t("push_enabled"));
      }
    } catch (e) {
      toast.error(e.message || t("push_failed"));
    }
    setPushBusy(false);
  };

  const load = async () => {
    try {
      const [a, n, tgRes] = await Promise.all([
        api.get("/alerts"),
        api.get("/notifications"),
        api.get("/telegram/status").catch(() => ({ data: { linked: false } })),
      ]);
      setAlerts(a.data);
      setNotifications(n.data);
      setTg(tgRes.data);
      // Pre-fetch the one-time deep link only if the user is not linked.
      // Rendering this as a real <a href> (instead of opening via JS after
      // an async await) lets mobile browsers hand the t.me URL straight to
      // the Telegram app — no blank popup tab.
      if (!tgRes.data.linked) {
        try {
          const lr = await api.post("/telegram/link");
          setTgDeepLink(lr.data.deep_link);
        } catch (_e) {/* */}
      } else {
        setTgDeepLink("");
      }
    } catch (_e) {/* */}
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto py-20 px-6 text-center">
        <AlertCircle className="w-12 h-12 mx-auto text-[#C85A32]"/>
        <p className="mt-4 font-heading font-bold text-2xl">{t("login_required")}</p>
        <button onClick={login} className="mt-6 btn-primary" data-testid="alerts-login-button">{t("cta_login")}</button>
      </div>
    );
  }

  const addAlert = async () => {
    if (!keyword.trim()) return;
    try {
      await api.post("/alerts", { keyword, city: city || null });
      setKeyword(""); setCity("");
      toast.success(t("alert_added"));
      await load();
    } catch (_e) { toast.error("Klaida"); }
  };

  const delAlert = async (id) => {
    await api.delete(`/alerts/${id}`);
    load();
  };

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  const linkTelegram = () => {
    // When the user taps the anchor below, the browser/OS handles the
    // deep link directly (Telegram app opens on mobile). We start polling
    // here so the page flips to "Connected" automatically when the user
    // comes back. Polling auto-stops after 3 min.
    if (tgPolling) return;
    setTgPolling(true);
    const start = Date.now();
    const poll = setInterval(async () => {
      try {
        const s = await api.get("/telegram/status");
        if (s.data.linked) {
          setTg(s.data);
          toast.success(t("tg_connected"));
          clearInterval(poll);
          setTgPolling(false);
        } else if (Date.now() - start > 180000) {
          clearInterval(poll);
          setTgPolling(false);
        }
      } catch (_e) {/* */}
    }, 3000);
  };

  const unlinkTelegram = async () => {
    try {
      await api.post("/telegram/unlink");
      setTg({ ...tg, linked: false });
      setTgDeepLink("");
      toast.success(t("tg_disconnected"));
      // Re-fetch so a fresh deep link is available if the user re-links.
      load();
    } catch (_e) { toast.error("Klaida"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <div className="bg-[#2A5C38] text-white rounded-md p-6 mb-6 hard-shadow">
        <div className="flex items-start gap-3">
          <Bell className="w-6 h-6 flex-shrink-0 mt-0.5"/>
          <div>
            <h1 className="text-2xl font-heading font-bold">{t("smart_alerts_title")}</h1>
            <p className="text-white/85 text-sm mt-1">{t("smart_alerts_sub")}</p>
          </div>
        </div>
      </div>

      {/* Web Push notifications card — works in-browser, no Telegram needed.
          Especially useful for desktop users + iOS users who installed the
          PWA to home screen. */}
      {push.supported && (
        <div
          className={`card-surface p-5 mb-4 flex flex-col sm:flex-row sm:items-center gap-4 ${
            push.subscribed ? "border-l-4 border-l-[#2A5C38]" : ""
          }`}
          data-testid="push-card"
        >
          <div className="flex-1">
            <h2 className="font-heading font-bold text-lg inline-flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-[#C85A32]" />
              {t("push_title")}
              {push.subscribed && (
                <span className="text-xs font-normal text-[#2A5C38] inline-flex items-center gap-1">
                  <Check className="w-4 h-4" />
                  {t("push_status_on")}
                </span>
              )}
            </h2>
            <p className="text-sm text-[#4A4A45] mt-1">
              {push.subscribed ? t("push_desc_on") : t("push_desc_off")}
            </p>
          </div>
          <button
            data-testid="push-toggle-button"
            onClick={togglePush}
            disabled={pushBusy}
            className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm transition-colors disabled:opacity-50 ${
              push.subscribed
                ? "border border-[#D1CFC7] text-[#4A4A45] hover:bg-[#F0EFEB]"
                : "bg-[#C85A32] text-white hover:bg-[#A84A28]"
            }`}
          >
            <Smartphone className="w-4 h-4" />
            {pushBusy ? "..." : push.subscribed ? t("push_disable") : t("push_enable")}
          </button>
        </div>
      )}

      {/* Telegram link card — lets the user get matched-listing alerts as push
          notifications to their phone via @StopChlamasBot. */}
      <div
        className={`card-surface p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-4 ${
          tg.linked ? "border-l-4 border-l-[#2A5C38]" : ""
        }`}
        data-testid="telegram-card"
      >
        <div className="flex-1">
          <h2 className="font-heading font-bold text-lg inline-flex items-center gap-2">
            <Send className="w-5 h-5 text-[#229ED9]" />
            {t("tg_title")}
            {tg.linked && (
              <span className="text-xs font-normal text-[#2A5C38] inline-flex items-center gap-1">
                <Check className="w-4 h-4" />
                {t("tg_status_linked")}
              </span>
            )}
          </h2>
          <p className="text-sm text-[#4A4A45] mt-1">
            {tg.linked ? t("tg_linked_desc") : t("tg_unlinked_desc")}
          </p>
        </div>
        {tg.linked ? (
          <button
            data-testid="telegram-unlink-button"
            onClick={unlinkTelegram}
            className="px-4 py-2.5 rounded-md font-semibold text-sm border border-[#D1CFC7] text-[#4A4A45] hover:bg-[#F0EFEB] transition-colors"
          >
            {t("tg_disconnect")}
          </button>
        ) : tgDeepLink ? (
          // Real anchor so mobile OS hands the t.me URL to the Telegram app
          // directly. No window.open, no blank tab, no popup blockers.
          <a
            data-testid="telegram-link-button"
            href={tgDeepLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={linkTelegram}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm bg-[#229ED9] text-white hover:bg-[#1c87b7] transition-colors no-underline"
          >
            <Send className="w-4 h-4" />
            {tgPolling ? t("tg_waiting") : t("tg_connect")}
          </a>
        ) : (
          <button
            disabled
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm bg-[#229ED9]/60 text-white cursor-wait"
          >
            <Send className="w-4 h-4" />
            ...
          </button>
        )}
      </div>

      <div className="card-surface p-5 mb-8">
        <h2 className="font-heading font-bold text-lg mb-3">{t("add_alert")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input
            data-testid="alert-keyword-input"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder={t("keyword")}
            className="bg-white border border-[#D1CFC7] rounded-md px-3 py-2.5 outline-none focus:ring-2 focus:ring-[#C85A32]"
          />
          <CityAutocomplete value={city} onChange={setCity} testId="alert-city-input" placeholder={t("all_cities")} />
          <button data-testid="alert-add-button" onClick={addAlert} className="btn-primary inline-flex items-center justify-center gap-2">
            <Plus className="w-4 h-4"/>{t("add_alert")}
          </button>
        </div>
      </div>

      {/* Notifications */}
      <h2 className="font-heading font-bold text-xl mb-3">{t("your_notifications")}</h2>
      {notifications.length === 0 ? (
        <div className="border border-dashed border-[#D1CFC7] rounded-md p-8 text-center text-[#7A7A73]" data-testid="notifications-empty">
          {t("notifications_empty")}
        </div>
      ) : (
        <div className="space-y-2 mb-8">
          {notifications.map((n) => (
            <Link
              key={n.id}
              to={`/listing/${n.listing_id}`}
              onClick={() => markRead(n.id)}
              data-testid={`notif-${n.id}`}
              className={`card-surface flex items-center gap-3 p-3 ${!n.is_read ? "border-[#C85A32] bg-[#FAF9F6]" : ""}`}
            >
              <div className={`w-2 h-2 rounded-full ${!n.is_read ? "bg-[#C85A32] pulse-dot" : "bg-transparent"}`}/>
              <div className="flex-1">
                <p className="text-sm text-[#7A7A73]">{t("alert_match")}: <strong className="text-[#C85A32]">{n.keyword}</strong></p>
                <p className="font-heading font-bold text-[#1A1A18]">{n.listing_title}</p>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Alerts */}
      <h2 className="font-heading font-bold text-xl mb-3">{t("your_alerts")}</h2>
      {alerts.length === 0 ? (
        <div className="border border-dashed border-[#D1CFC7] rounded-md p-8 text-center text-[#7A7A73]" data-testid="alerts-empty">
          —
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.map((a) => (
            <div key={a.id} className="card-surface flex items-center gap-3 p-3" data-testid={`alert-${a.id}`}>
              <Tag className="w-4 h-4 text-[#C85A32]"/>
              <div className="flex-1">
                <p className="font-heading font-bold text-[#1A1A18]">{a.keyword}</p>
                {a.city && <p className="text-xs text-[#7A7A73] inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{a.city}</p>}
              </div>
              <button data-testid={`alert-del-${a.id}`} onClick={() => delAlert(a.id)} className="p-2 hover:bg-[#F0EFEB] rounded text-[#7A7A73]">
                <Trash2 className="w-4 h-4"/>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
